// The roadmap timeline (kanban d71a3beb, owner-approved round 4 on review
// bdebb0e3): our own SVG, no chart library. Lanes by team, a fixed label
// column with the expand chevrons (the 2026-09-27 complaint: "where the
// chevron is"), horizon presets that fit the data instead of a Week view
// scrolled to today, bars only between stated dates, a placeholder strip for
// the undated, sub-actions as an indented tree, dependencies as arrows, and
// every glyph hoverable and clickable into the initiative pop-up.
//
// Layout math lives in roadmapTimelineUtils.ts; this file only draws.
//
// 2026-10-05 report ("it's just busy"): the chart opens on Simple (bars, target
// diamonds, today) and the rest of the glyphs sit behind the Detail control;
// see roadmapTimelineDetail.ts. In Simple a row's dependency arrows still
// appear while that row is hovered.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import type { InitiativeState, RoadmapEvent } from '../../services/roadmapReports';
import { ACTION_STATUS_LABELS, HEALTH_LABELS, STATUS_LABELS, type TeamColors } from './roadmapViewUtils';
import {
  actionAnchorKey,
  arrowPath,
  buildRows,
  chartHeight,
  clampSpan,
  datesInWords,
  DEFAULT_HORIZON,
  DELTA_PROJECTS_LANE,
  dependencyEdges,
  expandableKeys,
  fmtDay,
  fmtDayYear,
  HEALTH_COLORS,
  HEADER_HEIGHT,
  HORIZON_LABELS,
  horizonWindow,
  inWindow,
  isDeltaProject,
  makeScale,
  monthFormat,
  movementDates,
  parseDay,
  ROW_HEIGHT,
  rowY,
  splitUndated,
  subActionChip,
  tickSpec,
  timeMonth,
  type Anchor,
  type DeltaLaneMode,
  type HorizonPreset,
} from './roadmapTimelineUtils';
import { DETAIL_LABELS, readDetail, writeDetail, type DetailMode } from './roadmapTimelineDetail';
import { readDeltaLane, saveDeltaLane } from './roadmapDeltaLanePref';
import styles from './RoadmapTimeline.module.css';

export type ModalSection =
  | 'summary'
  | 'dates'
  | 'decisions'
  | 'waiting'
  | 'holds'
  | 'people'
  | 'evidence'
  | 'questions'
  | 'history';

interface Props {
  initiatives: InitiativeState[];
  events?: RoadmapEvent[];
  teamColors?: TeamColors;
  /** Open the initiative pop-up, optionally scrolled to a section. */
  onSelect?: (key: string, section?: ModalSection, action?: string) => void;
  /** The next scheduled check-in, drawn as a second vertical line. */
  checkIn?: { date: string; label: string } | null;
  /** Draw done sub-actions too (the page's "show done / dropped" checkbox). */
  showDone?: boolean;
  /** Done initiatives kept only because shown work still lists them as a blocker (timelineSet). */
  retained?: ReadonlySet<string>;
  /** Injectable clock for tests. */
  today?: string;
  /** Teams the page is filtered to: a shared initiative is drawn in one of their lanes. */
  filterTeams?: readonly string[];
}

interface Tip {
  content: ReactNode;
  x: number;
  y: number;
}

const HATCH_ID = 'roadmap-timeline-hatch';
const ARROW_ID = 'roadmap-timeline-arrow';
const NONE_RETAINED: ReadonlySet<string> = new Set();
const RETAINED_OPACITY = 0.45;
const HIDDEN_DONE_HINT = 'Done sub-actions are hidden. Tick "show done / dropped" to see them.';

function nextMondayCheckIn(today: Date): { date: string; label: string } {
  // The weekly AI/Code check-in is Monday 1 pm Eastern (owner calendar).
  const d = new Date(today);
  const dow = d.getDay();
  const add = dow === 1 ? 0 : (8 - dow) % 7;
  d.setDate(d.getDate() + add);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { date: iso, label: `Check-in ${fmtDay(d)}, 1 pm ET` };
}

/**
 * The timeline: a chart of what has dates, and under it a plain list of what
 * does not.
 */
export function TimelineChart(props: Props) {
  const today = useMemo(() => parseDay(props.today ?? new Date().toISOString().slice(0, 10)) as Date, [props.today]);
  const retained = props.retained ?? NONE_RETAINED;
  const { drawn, undated } = useMemo(() => {
    const split = splitUndated(props.initiatives, today);
    // A done initiative kept only as a blocker is not something Merlin asks
    // dates for; off the chart it has nothing to show.
    return { drawn: split.drawn, undated: split.undated.filter((i) => !retained.has(i.initiative_key)) };
  }, [props.initiatives, today, retained]);
  return (
    <>
      {drawn.length > 0 ? (
        <TimelineBars {...props} initiatives={drawn} />
      ) : (
        <p className={styles.undatedNote2} data-testid="timeline-nothing-dated">
          Nothing shown here has a date yet, so there is nothing to draw.
        </p>
      )}
      {undated.length > 0 && (
        <section className={styles.undatedPanel} aria-label="Initiatives with no dates yet" data-testid="timeline-undated">
          <h3>No dates yet ({undated.length})</h3>
          <p>Nobody has stated a start or a target for these, so they are not on the chart. Merlin is asking.</p>
          <ul>
            {undated.map((i) => (
              <li key={i.initiative_key}>
                <button type="button" onClick={() => props.onSelect?.(i.initiative_key, 'dates')}>{i.title}</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function TimelineBars({
  initiatives, events = [], teamColors = {}, onSelect, checkIn, showDone = false, retained = NONE_RETAINED, today: todayIso, filterTeams,
}: Props) {
  const today = useMemo(() => parseDay(todayIso ?? new Date().toISOString().slice(0, 10)) as Date, [todayIso]);
  const [horizon, setHorizon] = useState<HorizonPreset>(DEFAULT_HORIZON);
  const [deltaLane, setDeltaLaneState] = useState<DeltaLaneMode>(() => readDeltaLane());
  const setDeltaLane = useCallback((mode: DeltaLaneMode) => {
    setDeltaLaneState(mode);
    saveDeltaLane(mode);
  }, []);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [hoverEdge, setHoverEdge] = useState<number | null>(null);
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailMode>(readDetail);
  const full = detail === 'full';
  const chooseDetail = useCallback((mode: DetailMode) => {
    setDetail(mode);
    writeDetail(mode);
  }, []);
  const [tip, setTip] = useState<Tip | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useState(900);

  useLayoutEffect(() => {
    const host = chartRef.current;
    if (!host) return;
    const measure = () => setWidth(Math.max(700, host.clientWidth || 900));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  const expandable = useMemo(() => expandableKeys(initiatives, showDone), [initiatives, showDone]);
  const allOpen = expandable.length > 0 && expandable.every((k) => expanded.has(k));
  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const rows = useMemo(
    () => buildRows(initiatives, { expanded, deltaLane, today, teamColors, preferTeams: filterTeams, includeDone: showDone, retained }),
    [initiatives, expanded, deltaLane, today, teamColors, filterTeams, showDone, retained],
  );
  const spans = useMemo(() => rows.flatMap((r) => (r.kind === 'init' ? [r.span] : [])), [rows]);
  const win = useMemo(() => horizonWindow(horizon, today, spans), [horizon, today, spans]);
  const x = useMemo(() => makeScale(win, width), [win, width]);
  const ticks = useMemo(() => tickSpec(win), [win]);
  const byKey = useMemo(() => new Map(initiatives.map((i) => [i.initiative_key, i])), [initiatives]);
  const hasDeltaProjects = useMemo(() => initiatives.some(isDeltaProject), [initiatives]);
  const checkInLine = useMemo(() => {
    const c = checkIn === undefined ? nextMondayCheckIn(today) : checkIn;
    if (!c) return null;
    const d = parseDay(c.date);
    return d ? { ...c, day: d } : null;
  }, [checkIn, today]);

  const height = chartHeight(rows.length);

  // Anchors (bar edges) for arrows, computed once per render.
  const anchors = useMemo(() => {
    const map = new Map<string, Anchor>();
    rows.forEach((r, idx) => {
      const cy = rowY(idx) + ROW_HEIGHT / 2;
      if (r.kind === 'init') {
        const c = clampSpan(x, r.span.start, r.span.end, win);
        if (c) map.set(r.initiative.initiative_key, { ...c, cy, placeholder: r.span.placeholder });
      } else if (r.kind === 'act') {
        const parent = map.get(r.initiative.initiative_key);
        if (r.span.dated) {
          const s = r.span.start ?? (r.span.end as Date);
          const e = r.span.end ?? (r.span.start as Date);
          const c = clampSpan(x, s, e, win);
          if (c) map.set(actionAnchorKey(r.initiative.initiative_key, r.action.key), { ...c, cy, placeholder: false });
        } else if (parent) {
          map.set(actionAnchorKey(r.initiative.initiative_key, r.action.key), { x1: parent.x1 + 12, x2: parent.x1 + 100, cy, placeholder: true });
        }
      }
    });
    return map;
  }, [rows, x, win]);
  const edges = useMemo(() => dependencyEdges(rows, anchors, byKey), [rows, anchors, byKey]);
  const highlighted = hoverEdge === null ? null : edges[hoverEdge];

  const showTip = useCallback((content: ReactNode, ev: ReactMouseEvent) => {
    const panel = panelRef.current;
    const rect = panel?.getBoundingClientRect();
    setTip({ content, x: ev.clientX - (rect?.left ?? 0) + 14, y: ev.clientY - (rect?.top ?? 0) + 14 });
  }, []);
  const moveTip = useCallback((ev: ReactMouseEvent) => {
    setTip((t) => {
      if (!t) return t;
      const rect = panelRef.current?.getBoundingClientRect();
      return { ...t, x: ev.clientX - (rect?.left ?? 0) + 14, y: ev.clientY - (rect?.top ?? 0) + 14 };
    });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);
  useEffect(() => () => setTip(null), []);

  // The action key is passed only when there is one, so an initiative glyph
  // still calls onSelect(key, section) exactly as before.
  const open = useCallback(
    (key: string, section?: ModalSection, action?: string) => (action === undefined ? onSelect?.(key, section) : onSelect?.(key, section, action)),
    [onSelect],
  );
  /** Titles of the shown initiatives that still list `key` as a blocker. */
  const waitingOn = useCallback(
    (key: string) => initiatives.filter((i) => i.depends_on.includes(key)).map((i) => i.title),
    [initiatives],
  );

  if (rows.length === 0) return null;

  const glyphProps = (content: ReactNode, key: string, section: ModalSection, action?: string) => ({
    className: styles.glyph,
    tabIndex: 0,
    role: 'button' as const,
    onMouseEnter: (ev: ReactMouseEvent) => { setHoverRow(key); showTip(content, ev); },
    onMouseMove: moveTip,
    onMouseLeave: () => { setHoverRow(null); hideTip(); },
    onClick: (ev: ReactMouseEvent) => {
      ev.stopPropagation();
      open(key, section, action);
    },
    onKeyDown: (ev: ReactKeyboardEvent) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        open(key, section, action);
      }
    },
  });

  const gridTicks = x.ticks(ticks.grid);
  const monthTicks = x.ticks(timeMonth);
  const lowerTicks = ticks.lower ? x.ticks(ticks.lower) : [];
  const teams = Object.entries(teamColors);

  return (
    <section ref={panelRef} className={styles.panel} aria-label="Roadmap timeline" data-testid="roadmap-timeline">
      <div className={styles.head}>
        <h2>Timeline</h2>
        <div className={styles.controls}>
          <span className={styles.segment} role="group" aria-label="Horizon">
            {(Object.keys(HORIZON_LABELS) as HorizonPreset[]).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={horizon === p}
                className={horizon === p ? styles.segmentOn : undefined}
                onClick={() => setHorizon(p)}
              >
                {HORIZON_LABELS[p]}
              </button>
            ))}
          </span>
          <span className={styles.segment} role="group" aria-label="Detail">
            {(Object.keys(DETAIL_LABELS) as DetailMode[]).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={detail === m}
                className={detail === m ? styles.segmentOn : undefined}
                title={m === 'simple'
                  ? 'Bars, target dates and today. Hover a row to see what it depends on.'
                  : 'Also decisions, movement, outside blockers, every dependency arrow and the next check-in.'}
                onClick={() => chooseDetail(m)}
              >
                {DETAIL_LABELS[m]}
              </button>
            ))}
          </span>
          {hasDeltaProjects && (
            <span className={styles.segment} role="group" aria-label="Delta projects lane">
              <button type="button" aria-pressed={deltaLane === 'team'} className={deltaLane === 'team' ? styles.segmentOn : undefined} onClick={() => setDeltaLane('team')}>
                Delta projects: inside Delta team
              </button>
              <button type="button" aria-pressed={deltaLane === 'own'} className={deltaLane === 'own' ? styles.segmentOn : undefined} onClick={() => setDeltaLane('own')}>
                own lane
              </button>
            </span>
          )}
          {expandable.length > 0 && (
            <button
              type="button"
              className={styles.expandBtn}
              onClick={() => setExpanded(allOpen ? new Set() : new Set(expandable))}
            >
              {allOpen ? 'Collapse all' : `Expand all (${expandable.length} with sub-actions)`}
            </button>
          )}
        </div>
      </div>
      {/* Twelve legend entries took two lines above every chart; they sit behind Key now. */}
      <details className={styles.legendKey}>
        <summary>Key</summary>
        <div className={styles.legend} aria-label="Legend">
          {teams.map(([team, color]) => (
            <span key={team} className={styles.legendItem}>
              <i className={styles.sw} style={{ background: color }} /> {team}
            </span>
          ))}
          <span className={styles.legendItem}><i className={`${styles.sw} ${styles.swPlaceholder}`} /> no dates yet (dotted, first mention to today)</span>
          <span className={styles.legendItem}><i className={`${styles.sw} ${styles.swInferred}`} /> dashed edge: not stated, inferred</span>
          {!showDone && (
            <span className={styles.legendItem}><i className={`${styles.sw} ${styles.swRetained}`} /> done, kept because shown work still lists it as a blocker</span>
          )}
          <span className={styles.legendItem}><i className={styles.swDiamond} /> target date</span>
          {full && <span className={styles.legendItem}><i className={styles.swDecision} /> decision</span>}
          {full && <span className={styles.legendItem}><i className={styles.swTick} /> movement (a run recorded a change)</span>}
          {full && <span className={styles.legendItem}><i className={`${styles.sw} ${styles.swHatch}`} /> waiting on someone outside</span>}
          <span className={styles.legendItem}><i className={styles.swArrow} /> {full ? 'depends on (hover to trace)' : 'depends on (shown while you hover a row)'}</span>
          <span className={styles.legendItem}><i className={styles.swLate} /> late</span>
          <span className={styles.legendItem}><i className={styles.swRisk} /> at risk or stuck</span>
          {!full && <span className={styles.legendItem}>Full detail adds decisions, movement, outside blockers and the next check-in.</span>}
        </div>
      </details>

      <div className={styles.split}>
        <div className={styles.labels} style={{ paddingTop: HEADER_HEIGHT }}>
          {rows.map((r) => {
            const key = r.kind === 'lane' ? `lane-${r.lane}` : r.kind === 'init' ? r.initiative.initiative_key : actionAnchorKey(r.initiative.initiative_key, r.action.key);
            const hi = highlighted && r.kind !== 'lane' && (highlighted.fromRow === r.initiative.initiative_key || highlighted.toRow === r.initiative.initiative_key);
            if (r.kind === 'lane') {
              return (
                <div key={key} className={`${styles.row} ${styles.laneRow}`} style={{ height: ROW_HEIGHT }}>
                  <i className={styles.hl} style={{ background: r.color }} />
                  <span className={styles.laneName}>{r.lane}</span>
                  <span className={styles.laneCount}>{r.count}</span>
                </div>
              );
            }
            if (r.kind === 'init') {
              const i = r.initiative;
              // Collapsed: "1/7" (sentence on hover). Expanded: how many done
              // sub-actions the row is not listing, so the count stays honest.
              const chip = r.expanded
                ? (r.hiddenDone > 0 ? { short: `${r.hiddenDone} done hidden`, long: HIDDEN_DONE_HINT } : null)
                : subActionChip(i.actions);
              return (
                <div
                  key={key}
                  className={`${styles.row} ${hi ? styles.rowHi : ''} ${r.retained ? styles.rowRetained : ''}`}
                  style={{ height: ROW_HEIGHT }}
                  data-testid={`timeline-row-${i.initiative_key}`}
                  onMouseEnter={(ev) => {
                    setHoverRow(i.initiative_key);
                    showTip(<RowTip i={i} words={datesInWords(r.span, i)} lane={r.lane} waiting={r.retained ? waitingOn(i.initiative_key) : undefined} />, ev);
                  }}
                  onMouseMove={moveTip}
                  onMouseLeave={() => { setHoverRow(null); hideTip(); }}
                >
                  {r.actions.length > 0 ? (
                    <button
                      type="button"
                      className={styles.toggle}
                      aria-label={`${r.expanded ? 'Hide' : 'Show'} ${r.actions.length} sub-actions of ${i.title}`}
                      aria-expanded={r.expanded}
                      onClick={(ev) => { ev.stopPropagation(); toggle(i.initiative_key); }}
                    >
                      {r.expanded ? '▾' : '▸'}
                    </button>
                  ) : (
                    <span className={styles.toggleNone} aria-hidden="true" />
                  )}
                  <i className={styles.hl} style={{ background: HEALTH_COLORS[i.health] }} title={HEALTH_LABELS[i.health]} />
                  <button type="button" className={styles.name} title={i.title} onClick={() => open(i.initiative_key, 'summary')}>
                    {i.title}
                  </button>
                  {chip && <span className={styles.chip} title={chip.long}>{chip.short}</span>}
                </div>
              );
            }
            return (
              <div
                key={key}
                className={`${styles.row} ${styles.subRow} ${hi ? styles.rowHi : ''} ${r.retained ? styles.rowRetained : ''}`}
                style={{ height: ROW_HEIGHT }}
                title={r.retained ? 'Done. Kept because an unfinished sub-action still waits on it.' : undefined}
              >
                <span className={styles.branch} aria-hidden="true">{r.last ? '└' : '├'}</span>
                <button
                  type="button"
                  className={styles.name}
                  title={r.action.title}
                  onClick={() => open(r.initiative.initiative_key, 'dates', r.action.key)}
                  data-action="roadmap_action_open"
                  data-target-initiative-key={r.initiative.initiative_key}
                  data-target-action-key={r.action.key}
                >
                  {r.action.title}
                </button>
                <span className={styles.chip}>{ACTION_STATUS_LABELS[r.action.status] ?? r.action.status}</span>
              </div>
            );
          })}
        </div>

        <div ref={chartRef} className={styles.chart}>
          <svg width={width} height={height} className={styles.svg} role="img" aria-label="Timeline chart">
            <defs>
              <pattern id={HATCH_ID} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                <rect width="2" height="6" fill="#c98a00" opacity="0.7" />
              </pattern>
              <marker id={ARROW_ID} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M0 0 L10 5 L0 10 z" fill="#4a3aa7" />
              </marker>
            </defs>

            {rows.map((r, idx) => r.kind === 'lane' && (
              <rect key={`band-${r.lane}`} className={styles.laneBand} x={0} y={rowY(idx)} width={width} height={ROW_HEIGHT} />
            ))}
            {gridTicks.map((d) => (
              <line key={`g-${d.getTime()}`} className={styles.grid} x1={x(d)} x2={x(d)} y1={HEADER_HEIGHT} y2={height} />
            ))}
            {monthTicks.map((d) => (
              <text key={`m-${d.getTime()}`} className={styles.monthLabel} x={x(d) + 4} y={18}>{monthFormat(d)}</text>
            ))}
            {lowerTicks.map((d) => (
              <text key={`w-${d.getTime()}`} className={styles.tickLabel} x={x(d) + 3} y={38}>{ticks.lowerFormat(d)}</text>
            ))}

            {/* today and check-in lines */}
            {inWindow(today, win) && (
              <g>
                <line className={styles.todayLine} x1={x(today)} x2={x(today)} y1={HEADER_HEIGHT - 6} y2={height} />
                <line
                  onMouseEnter={(ev) => showTip(<><b>Today</b><div>{fmtDayYear(today)}. Bars to the left of this line are elapsed.</div></>, ev)}
                  onMouseMove={moveTip}
                  onMouseLeave={hideTip}
                  x1={x(today)} x2={x(today)} y1={HEADER_HEIGHT - 6} y2={height} stroke="transparent" strokeWidth={8}
                  data-testid="today-line"
                />
                <text className={styles.todayLabel} x={x(today) + 4} y={height - 4}>today</text>
              </g>
            )}
            {full && checkInLine && inWindow(checkInLine.day, win) && (
              <g>
                <line className={styles.checkInLine} x1={x(checkInLine.day)} x2={x(checkInLine.day)} y1={HEADER_HEIGHT} y2={height} />
                <line
                  onMouseEnter={(ev) => showTip(<><b>Next check-in</b><div>{checkInLine.label}. Merlin's questions for that meeting are on the questions tab.</div></>, ev)}
                  onMouseMove={moveTip}
                  onMouseLeave={hideTip}
                  x1={x(checkInLine.day)} x2={x(checkInLine.day)} y1={HEADER_HEIGHT} y2={height} stroke="transparent" strokeWidth={8}
                  data-testid="checkin-line"
                />
              </g>
            )}

            {/* dependency arrows */}
            {edges.map((e, n) => {
              const from = anchors.get(e.fromKey);
              const to = anchors.get(e.toKey);
              if (!from || !to) return null;
              // Simple draws an arrow only while one of its rows (or the arrow) is hovered.
              if (!full && hoverEdge !== n && e.fromRow !== hoverRow && e.toRow !== hoverRow) return null;
              return (
                <path
                  key={`edge-${n}`}
                  className={`${styles.dep} ${hoverEdge === n ? styles.depHi : ''}`}
                  d={arrowPath(from, to)}
                  markerEnd={`url(#${ARROW_ID})`}
                  strokeDasharray={e.dashed ? '4 3' : undefined}
                  onMouseEnter={(ev) => { setHoverEdge(n); showTip(<><b>Depends on</b><div>{e.label}</div>{e.dashed && <div className={styles.muted}>One end has no stated date; the arrow is anchored to the placeholder edge.</div>}</>, ev); }}
                  onMouseMove={moveTip}
                  onMouseLeave={() => { setHoverEdge(null); hideTip(); }}
                  onClick={(ev) => { ev.stopPropagation(); open(e.openKey, 'waiting'); }}
                  data-testid={`dep-${e.fromKey}-${e.toKey}`}
                />
              );
            })}
            {/* bars and glyphs */}
            {rows.map((r, idx) => {
              if (r.kind === 'lane') return null;
              const y = rowY(idx);
              const cy = y + ROW_HEIGHT / 2;
              if (r.kind === 'init') {
                const i = r.initiative;
                const a = anchors.get(i.initiative_key);
                if (!a) return null;
                const stroke = r.span.placeholder ? '#8a8985' : HEALTH_COLORS[i.health] ?? r.color;
                const strong = i.health === 'late' || i.health === 'at_risk' || i.health === 'stuck';
                const dash = r.span.placeholder ? undefined : (!r.span.startStated || !r.span.endStated) ? '6 3' : undefined;
                const barH = r.expanded ? 8 : 16;
                const elapsedX = Math.min(x(today), a.x2);
                const moves = movementDates(events, i.initiative_key).filter((m) => inWindow(m.date, win));
                const decisions = (i.decisions ?? []).flatMap((d) => {
                  const day = parseDay(d.on);
                  return inWindow(day, win) ? [{ ...d, day }] : [];
                });
                const words = datesInWords(r.span, i);
                return (
                  <g
                    key={i.initiative_key}
                    data-testid={`bar-${i.initiative_key}`}
                    data-retained={r.retained ? 'true' : undefined}
                    opacity={r.retained ? RETAINED_OPACITY : undefined}
                  >
                    <rect
                      {...glyphProps(<RowTip i={i} words={words} lane={r.lane} waiting={r.retained ? waitingOn(i.initiative_key) : undefined} />, i.initiative_key, 'summary')}
                      x={a.x1} y={cy - barH / 2} width={Math.max(4, a.x2 - a.x1)} height={barH} rx={4}
                      fill={r.span.placeholder ? 'transparent' : `${r.color}${r.expanded ? '33' : '55'}`}
                      stroke={r.span.placeholder ? 'none' : stroke} strokeWidth={strong ? 2 : 1.2} strokeDasharray={dash}
                      data-testid={`span-${i.initiative_key}`}
                      data-placeholder={r.span.placeholder ? 'true' : undefined}
                    />
                    {/* No stated dates: a faint dotted line from first mention to today,
                        so the dated bars are the only bars on the chart (the rect above
                        stays as the hover / click target). */}
                    {r.span.placeholder && (
                      <line
                        x1={a.x1} x2={Math.max(a.x1 + 4, a.x2)} y1={cy} y2={cy}
                        stroke="#b4b2ab" strokeWidth={2} strokeDasharray="2 5" strokeLinecap="round" pointerEvents="none"
                        data-testid={`undated-line-${i.initiative_key}`}
                      />
                    )}
                    {r.span.dated && !r.expanded && elapsedX > a.x1 && (
                      <rect x={a.x1} y={cy - 8} width={elapsedX - a.x1} height={16} rx={4} fill={r.color} opacity={0.85} pointerEvents="none" />
                    )}
                    {full && i.external_blocker && (
                      <rect
                        {...glyphProps(<><b>Waiting on someone outside</b><div>{i.external_blocker}</div></>, i.initiative_key, 'waiting')}
                        x={Math.max(x(today), a.x1)} y={cy - 8} width={Math.max(8, a.x2 - Math.max(x(today), a.x1))} height={16} rx={4}
                        fill={`url(#${HATCH_ID})`} stroke="none"
                        data-testid={`blocker-${i.initiative_key}`}
                      />
                    )}
                    {r.span.target && inWindow(r.span.target, win) && (
                      <path
                        {...glyphProps(<><b>Target {fmtDayYear(r.span.target)}</b><div>Stated by the group. {HEALTH_LABELS[i.health]}.</div></>, i.initiative_key, 'dates')}
                        d={`M${x(r.span.target)} ${cy - 10} l6 10 l-6 10 l-6 -10 z`}
                        fill={HEALTH_COLORS[i.health]} stroke={HEALTH_COLORS[i.health]} strokeWidth={1.5}
                        data-testid={`target-${i.initiative_key}`}
                      />
                    )}
                    {full && moves.map((m) => (
                      <g key={`mv-${m.date.getTime()}`}>
                        <line x1={x(m.date)} x2={x(m.date)} y1={cy + 9} y2={cy + 14} stroke="#52514e" strokeWidth={1.5} pointerEvents="none" />
                        <rect
                          {...glyphProps(<><b>Movement {fmtDayYear(m.date)}</b><div>A roadmap run changed: {m.fields.join(', ') || 'details refreshed'}</div></>, i.initiative_key, 'history')}
                          x={x(m.date) - 4} y={cy + 7} width={8} height={9} fill="transparent"
                          data-testid={`move-${i.initiative_key}-${m.date.getTime()}`}
                        />
                      </g>
                    ))}
                    {full && decisions.map((d, n) => (
                      <path
                        key={`dec-${n}`}
                        {...glyphProps(<><b>Decision {fmtDayYear(d.day)}</b><div>{d.what}</div><div className={styles.muted}>{d.who.join(', ')}</div></>, i.initiative_key, 'decisions')}
                        d={`M${x(d.day)} ${cy - 7} l6 7 l-6 7 l-6 -7 z`} fill="#0b0b0b"
                        data-testid={`decision-${i.initiative_key}-${n}`}
                      />
                    ))}
                  </g>
                );
              }
              // sub-action row
              const parent = anchors.get(r.initiative.initiative_key);
              const ak = actionAnchorKey(r.initiative.initiative_key, r.action.key);
              const a = anchors.get(ak);
              const act = r.action;
              const tipBody = (
                <>
                  <b>{act.title}</b>
                  <div>{ACTION_STATUS_LABELS[act.status] ?? act.status}{act.people.length ? ` · ${act.people.join(', ')}` : ''}</div>
                  <div>
                    {r.span.dated
                      ? `${r.span.start ? fmtDay(r.span.start) : ''}${r.span.end ? ` to ${fmtDay(r.span.end)}` : ''} (stated)`
                      : 'no dates stated'}
                  </div>
                  {r.retained && <div className={styles.muted}>Done. Kept because an unfinished sub-action still waits on it.</div>}
                </>
              );
              return (
                <g key={ak} data-retained={r.retained ? 'true' : undefined} opacity={r.retained ? RETAINED_OPACITY : undefined}>
                  {parent && (
                    <>
                      <line className={styles.connector} x1={parent.x1} x2={parent.x1} y1={cy - ROW_HEIGHT / 2} y2={r.last ? cy : cy + ROW_HEIGHT / 2} />
                      <line className={styles.connector} x1={parent.x1} x2={parent.x1 + 8} y1={cy} y2={cy} />
                    </>
                  )}
                  {r.span.dated && a && a.x2 > a.x1 + 6 && (
                    <rect
                      {...glyphProps(tipBody, r.initiative.initiative_key, 'dates', act.key)}
                      x={a.x1} y={cy - 5} width={a.x2 - a.x1} height={10} rx={5}
                      fill={act.status === 'done' ? r.color : `${r.color}44`} stroke={r.color}
                      data-testid={`action-${ak}`}
                    />
                  )}
                  {r.span.dated && a && a.x2 <= a.x1 + 6 && (
                    <circle
                      {...glyphProps(tipBody, r.initiative.initiative_key, 'dates', act.key)}
                      cx={a.x1} cy={cy} r={5} fill={act.status === 'done' ? r.color : '#fff'} stroke={r.color} strokeWidth={2}
                      data-testid={`action-${ak}`}
                    />
                  )}
                  {!r.span.dated && parent && (
                    <text
                      {...glyphProps(tipBody, r.initiative.initiative_key, 'dates', act.key)}
                      className={styles.undatedNote} x={parent.x1 + 12} y={cy + 4}
                      data-testid={`action-${ak}`}
                    >
                      no dates stated
                    </text>
                  )}
                </g>
              );
            })}

          </svg>
        </div>
      </div>
      {tip && (
        <div className={styles.tip} role="tooltip" style={{ left: tip.x, top: tip.y }} data-testid="timeline-tooltip">
          {tip.content}
        </div>
      )}
    </section>
  );
}

function RowTip({ i, words, lane, waiting }: { i: InitiativeState; words: string; lane: string; waiting?: string[] }) {
  return (
    <>
      <b>{i.title}</b>
      <div>{lane === DELTA_PROJECTS_LANE ? 'Delta project' : lane} · {STATUS_LABELS[i.status]} · {HEALTH_LABELS[i.health]}</div>
      <div>{words}</div>
      {waiting && <div>Done. Still listed as a blocker by {waiting.join(', ')}.</div>}
      <div className={styles.muted}>Click for the full breakdown</div>
    </>
  );
}
